import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Area, AreaChart } from 'recharts';

const HEPTEnhancedExecutiveDashboard = ({ onNavigate }) => {
  const [activeTab, setActiveTab] = useState('overview');

  // Strategic phase data with enhanced metrics
  const strategicPhases = [
    {
      phase: "IMMEDIATE",
      timeline: "0-6 months", 
      title: "Premium Private Market",
      population: "218,000 women (26.1%)",
      readiness: "89%",
      characteristics: [
        "Target: Women 25-45, university/postgraduate",
        "education, married with children, employed",
        "full-time, Lagos/Ogun urban areas"
      ],
      keyCharacteristics: [
        "Age: 25-45 years (peak 25-34: 37.4% high awareness)",
        "Education: University/postgraduate (39.6% high knowledge)",
        "Employment: Full-time employed (52.3% pay out-of-pocket)",
        "Location: Lagos State (43.9%), Ogun State (16.5%)",
        "Family: Married (84.2% readiness), has children (79.6% readiness)",
        "Healthcare: Visited provider within 12 months"
      ],
      strategy: "Facebook/Instagram ads targeting educated working mothers 25-45 in Lagos/Ogun. Premium hospital partnerships.",
      color: "bg-red-50 border-red-200 text-red-800",
      revenue: "₦2.5B projected",
      penetration: "15% target"
    },
    {
      phase: "EXPANSION", 
      timeline: "6-18 months",
      title: "Mass Market Foundation",
      population: "365,000 women (43.6%)",
      readiness: "75%",
      characteristics: [
        "Target: Women 18-35, secondary education,",
        "mixed employment, family financial support,",
        "never visited healthcare in 2+ years"
      ],
      keyCharacteristics: [
        "Age: 18-35 years (peak 15-24: 36.9% knowledge gaps)",
        "Education: Secondary school (53% of population)",
        "Employment: Mixed (unemployed 35%, part-time 13.3%)",
        "Payment: Family support (23%), government services (16%)",
        "Healthcare: No visits 3+ years or never (56.2% minimal access)",
        "Location: All Lagos/Ogun areas including suburban/peri-urban"
      ],
      strategy: "WhatsApp/SMS campaigns, radio ads, community health programs. Target secondary education women 18-35.",
      color: "bg-amber-50 border-amber-200 text-amber-800",
      revenue: "Public-private partnerships",
      penetration: "20% target"
    },
    {
      phase: "INTEGRATION",
      timeline: "12+ months", 
      title: "Male Partner Engagement",
      population: "222,000 men (26.5%)",
      readiness: "65%",
      characteristics: [
        "Target: Men 25-45, any education level,",
        "married/dating women 15-35, employed,",
        "workplace health program participants"
      ],
      keyCharacteristics: [
        "Age: 25-45 years (decision-making authority)",
        "Relationship: Married (37.5% age 25-34) or dating (18.6%)",
        "Education: All levels (42.7% have low HPV knowledge universally)",
        "Employment: Full-time (33.7%), self-employed (27.1%)",
        "Healthcare: Minimal engagement (51.7% vs 44.9% women)",
        "Influence: Partner health decisions, family planning involvement"
      ],
      strategy: "LinkedIn ads for working men 25-45, workplace health programs, couple-focused messaging.",
      color: "bg-emerald-50 border-emerald-200 text-emerald-800",
      revenue: "Partner channel development",
      penetration: "40% engagement improvement"
    }
  ];

  // Enhanced target segments with cross-analysis
  const targetSegments = [
    {
      id: "A1",
      profile: "Healthcare-Engaged High-Awareness", 
      awareness: "High (52%)",
      healthcareAccess: "Recent visits (27%)",
      vaccineIntent: "85%",
      popPercent: "14.0%",
      marketSize: "117k women",
      strategicValue: "Immediate Launch",
      valueColor: "bg-red-100 text-red-800",
      targeting: "Women 25-34, university degree, married, children, employed, Lagos/Ogun, visited healthcare within 12 months",
      priority: "🔴 IMMEDIATE"
    },
    {
      id: "A2", 
      profile: "Healthcare-Engaged Medium-Awareness",
      awareness: "Medium (45%)",
      healthcareAccess: "Recent visits (27%)", 
      vaccineIntent: "82%",
      popPercent: "12.1%",
      marketSize: "101k women",
      strategicValue: "Rapid Expansion", 
      valueColor: "bg-red-100 text-red-800",
      targeting: "Women 25-34, mixed education, recent healthcare engagement, moderate HPV knowledge",
      priority: "🔴 IMMEDIATE"
    },
    {
      id: "B1",
      profile: "Healthcare-Avoidant High-Awareness",
      awareness: "High (52%)",
      healthcareAccess: "Never visited (31%)",
      vaccineIntent: "75%", 
      popPercent: "16.1%",
      marketSize: "135k women",
      strategicValue: "Access Development",
      valueColor: "bg-amber-100 text-amber-800",
      targeting: "Women with high awareness but healthcare avoidance, mobile/home service delivery focus",
      priority: "🟡 SECONDARY"
    },
    {
      id: "B2",
      profile: "Healthcare-Avoidant Medium-Awareness",
      awareness: "Medium (45%)",
      healthcareAccess: "Never visited (31%)",
      vaccineIntent: "65%", 
      popPercent: "13.9%",
      marketSize: "116k women",
      strategicValue: "Education Focus",
      valueColor: "bg-emerald-100 text-emerald-800",
      targeting: "Women 18-35, secondary education, unemployed/part-time, family financial support, education campaigns needed",
      priority: "🟢 FUTURE"
    },
    {
      id: "C1",
      profile: "Male Partners High-Engagement",
      awareness: "Low (28%)",
      healthcareAccess: "Good access (26%)",
      vaccineIntent: "50%", 
      popPercent: "13.0%",
      marketSize: "109k men",
      strategicValue: "Partner Advocacy",
      valueColor: "bg-emerald-100 text-emerald-800",
      targeting: "Men 25-45, any education, married/dating, employed, Lagos/Ogun, influences partner health decisions",
      priority: "🟢 PARTNER CHANNEL"
    },
    {
      id: "C2",
      profile: "Young Adults High-Intent",
      awareness: "High (51%)",
      healthcareAccess: "Mixed access",
      vaccineIntent: "81%", 
      popPercent: "15.2%",
      marketSize: "127k mixed",
      strategicValue: "Future Market",
      valueColor: "bg-blue-100 text-blue-800",
      targeting: "Ages 15-24, mixed gender, high intent, prevention-focused, future market development",
      priority: "🔵 FUTURE MARKET"
    }
  ];

  // Market segmentation data for visualization
  const segmentReadinessData = [
    { segment: 'A1 - High Awareness/High Access', readiness: 85, size: 117000 },
    { segment: 'A2 - Med Awareness/High Access', readiness: 82, size: 101000 },
    { segment: 'B1 - High Awareness/Low Access', readiness: 75, size: 135000 },
    { segment: 'B2 - Med Awareness/Low Access', readiness: 65, size: 116000 },
    { segment: 'C1 - Male Partners', readiness: 50, size: 109000 },
    { segment: 'C2 - Young Adults', readiness: 81, size: 127000 }
  ];

  // Handle navigation
  const handleNavigation = (tabId) => {
    if (onNavigate) {
      onNavigate(tabId);
    }
  };

  const TabButton = ({ id, label, isActive, onClick }) => (
    <button
      onClick={() => onClick(id)}
      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
        isActive 
          ? 'bg-blue-600 text-white' 
          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="max-w-7xl mx-auto p-6 bg-white">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          MSD HEPT Nigeria Executive Summary
        </h1>
        <h2 className="text-xl text-gray-700 mb-4">
          Strategic Framework for HPV Vaccine Initiative Launch
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Population-adjusted insights from 2,845 survey respondents • Lagos/Ogun State • Ages 15-45
        </p>
        
        {/* Key Metrics Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-center">
            <div>
              <div className="text-2xl font-bold text-blue-700">71.4%</div>
              <div className="text-sm text-gray-600">High Vaccination Readiness</div>
            </div>
            <div>
              <div className="text-2xl font-bold text-red-700">798,000</div>
              <div className="text-sm text-gray-600">Target Population</div>
            </div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 mb-8 border-b border-gray-200 pb-4">
        <TabButton id="overview" label="Strategic Overview" isActive={activeTab === 'overview'} onClick={setActiveTab} />
        <TabButton id="segments" label="Market Segments" isActive={activeTab === 'segments'} onClick={setActiveTab} />
        <TabButton id="methodology" label="Methodology" isActive={activeTab === 'methodology'} onClick={setActiveTab} />
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div>
          {/* Strategic Market Entry Recommendations */}
          <div className="mb-10">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Strategic Market Entry Recommendations</h3>
            <div className="flex flex-row items-stretch gap-4">
              {strategicPhases.map((phase, index) => (
                <React.Fragment key={index}>
                  <div className={`flex-1 p-6 rounded-lg border-2 ${phase.color}`}>
                    <div className="mb-4">
                      <div className="text-xs font-bold mb-1">{phase.phase} • {phase.timeline}</div>
                      <h4 className="text-lg font-bold mb-2">{phase.title}</h4>
                      <div className="text-sm font-medium mb-1">{phase.population}</div>
                      <div className="text-sm font-medium mb-3">Vaccine Readiness: {phase.readiness}</div>
                      <p className="text-sm mb-4">
                        {phase.characteristics.join(' ')}
                      </p>
                    </div>
                    
                    <div className="mb-4">
                      <div className="text-xs font-bold mb-2">Ad Targeting Criteria:</div>
                      <ul className="text-xs space-y-1">
                        {phase.keyCharacteristics.map((char, idx) => (
                          <li key={idx}>• {char}</li>
                        ))}
                      </ul>
                    </div>

                    <div className="mb-4">
                      <div className="text-xs font-bold mb-2">Campaign Strategy:</div>
                      <p className="text-xs">{phase.strategy}</p>
                    </div>

                    <div className="bg-white bg-opacity-50 p-2 rounded text-xs">
                      <div><strong>Revenue Target:</strong> {phase.revenue}</div>
                      <div><strong>Penetration:</strong> {phase.penetration}</div>
                    </div>
                  </div>
                  {index < strategicPhases.length - 1 && (
                    <div className="flex items-center justify-center px-2">
                      <div className="text-3xl text-gray-400">→</div>
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>



          {/* Bottom Line Up Front */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-bold text-green-800 mb-2">Bottom Line Up Front</h3>
            <p className="text-green-700">
              The Lagos and Ogun State market presents a <strong>71.4% high vaccination readiness</strong> opportunity among engaged populations, 
              with <strong>four distinct strategic clusters</strong> requiring differentiated approaches based on knowledge levels, healthcare access patterns, and behavioral intent.
            </p>
            <p className="text-green-700 mt-2">
              <strong>Key Market Opportunity:</strong> 137,955 individuals (17.3% of target population) demonstrate behavioral intent engagement, 
              with 98,492 individuals (71.4%) ready for immediate vaccination programs.
            </p>
          </div>
        </div>
      )}

      {activeTab === 'segments' && (
        <div>
          {/* Primary Target Segments Analysis */}
          <div className="mb-10">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Primary Target Segments Analysis</h3>
            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse border border-gray-300">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium">ID</th>
                    <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium">Profile</th>
                    <th className="border border-gray-300 px-3 py-2 text-center text-sm font-medium">Vaccine Intent</th>
                    <th className="border border-gray-300 px-3 py-2 text-center text-sm font-medium">Pop %</th>
                    <th className="border border-gray-300 px-3 py-2 text-center text-sm font-medium">Market Size</th>
                    <th className="border border-gray-300 px-3 py-2 text-left text-sm font-medium">Ad Targeting Criteria</th>
                    <th className="border border-gray-300 px-3 py-2 text-center text-sm font-medium">Strategic Value</th>
                    <th className="border border-gray-300 px-3 py-2 text-center text-sm font-medium">Priority</th>
                  </tr>
                </thead>
                <tbody>
                  {targetSegments.map((segment, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-3 py-2 text-center font-bold">{segment.id}</td>
                      <td className="border border-gray-300 px-3 py-2 text-sm font-medium">{segment.profile}</td>
                      <td className="border border-gray-300 px-3 py-2 text-center text-sm font-medium">{segment.vaccineIntent}</td>
                      <td className="border border-gray-300 px-3 py-2 text-center text-sm">{segment.popPercent}</td>
                      <td className="border border-gray-300 px-3 py-2 text-center text-sm">{segment.marketSize}</td>
                      <td className="border border-gray-300 px-3 py-2 text-xs text-gray-700">{segment.targeting}</td>
                      <td className="border border-gray-300 px-3 py-2 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${segment.valueColor}`}>
                          {segment.strategicValue}
                        </span>
                      </td>
                      <td className="border border-gray-300 px-3 py-2 text-center text-sm">{segment.priority}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Market Size Distribution */}
            <div className="mt-8">
              <h4 className="text-lg font-medium mb-4">Market Size Distribution by Segment</h4>
              <ResponsiveContainer width="100%" height={400}>
                <PieChart>
                  <Pie
                    data={segmentReadinessData.map(item => ({
                      name: item.segment.split(' - ')[0],
                      value: item.size,
                      fullName: item.segment
                    }))}
                    cx="50%"
                    cy="50%"
                    labelLine={true}
                    label={({ name, value }) => `${name}: ${(value/1000).toFixed(0)}k`}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {segmentReadinessData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={['#ef4444', '#f59e0b', '#8b5cf6', '#10b981', '#3b82f6', '#6366f1'][index]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value, name, props) => [
                      `${value.toLocaleString()} people`,
                      props.payload.fullName
                    ]} 
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}





      {activeTab === 'methodology' && (
        <div>
          {/* Survey Methodology & Population Projection */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Survey Methodology & Population Projection</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">Survey Sample vs. Target Population</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Survey Sample Size:</span>
                    <span className="font-medium">2,845 total respondents</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Lagos/Ogun Focus:</span>
                    <span className="font-medium">1,727 respondents (ages 15-45)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Analysis Dataset:</span>
                    <span className="font-medium">838-1,570 by module</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-600">Target Population:</span>
                    <span className="font-bold text-blue-700">798,000 individuals</span>
                  </div>
                </div>
              </div>
              <div>
                <h4 className="font-semibold text-gray-800 mb-3">MRP Population Adjustment Process</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Age Reweighting:</span>
                    <span className="text-gray-700">Survey 48% vs actual 38% (15-24)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Education Reweighting:</span>
                    <span className="text-gray-700">Survey 32% vs actual 20% (university)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Gender Reweighting:</span>
                    <span className="text-gray-700">Survey 45% vs actual 49% (men)</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-600">Result:</span>
                    <span className="font-medium text-blue-700">Population-representative data</span>
                  </div>
                </div>
              </div>
            </div>
            <p className="text-xs text-gray-600 mt-4 italic">
              All percentages and market sizes reflect MRP-adjusted figures representing the 798,000 target population, not raw survey responses
            </p>
          </div>

          {/* Three-Dimensional Analysis Framework */}
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <h4 className="text-lg font-bold text-gray-800 mb-4">Three-Dimensional Analysis Framework</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-blue-50 border border-blue-200 p-4 rounded">
                <h5 className="font-medium text-blue-800 mb-2">1. Awareness & Knowledge Levels</h5>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>• HPV/cervical cancer knowledge gaps</li>
                  <li>• Demographic knowledge patterns</li>
                  <li>• Education level correlations</li>
                  <li>• Gender and age breakdowns</li>
                </ul>
              </div>
              <div className="bg-amber-50 border border-amber-200 p-4 rounded">
                <h5 className="font-medium text-amber-800 mb-2">2. Personal Health Experiences</h5>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Healthcare access patterns</li>
                  <li>• Screening behaviors</li>
                  <li>• Payment method distributions</li>
                  <li>• Service delivery preferences</li>
                </ul>
              </div>
              <div className="bg-green-50 border border-green-200 p-4 rounded">
                <h5 className="font-medium text-green-800 mb-2">3. Behavioral Intent</h5>
                <ul className="text-sm text-green-700 space-y-1">
                  <li>• Vaccination readiness levels</li>
                  <li>• Prevention interest patterns</li>
                  <li>• Service preferences</li>
                  <li>• Payment willingness</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Analysis Quality Control */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h4 className="text-lg font-bold text-gray-800 mb-4">Analysis Quality Control</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h5 className="font-medium text-gray-700 mb-2">Bias Corrections Applied</h5>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• University over-sampling: 37.6% → 20% adjusted</li>
                  <li>• Male under-sampling: 24% → 49% adjusted</li>
                  <li>• Age distribution: Survey skew corrected to match demographics</li>
                  <li>• Geographic weighting: Urban focus maintained</li>
                </ul>
              </div>
              <div>
                <h5 className="font-medium text-gray-700 mb-2">Analytical Rigor</h5>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Respondent-only analysis to avoid non-response bias</li>
                  <li>• Multi-step interactive analysis processes</li>
                  <li>• Cross-validation between data sources</li>
                  <li>• Edward Tufte design principles for data presentation</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigate to Detailed Analysis */}
      <div className="border-t border-gray-200 pt-8 mt-8">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Navigate to Detailed Analysis</h3>
        <p className="text-sm text-gray-600 mb-6">
          Explore detailed analysis across awareness levels, healthcare experiences, and vaccination intent
        </p>
        <div className="flex flex-wrap gap-4">
          <button 
            className="px-6 py-3 bg-blue-600 text-white rounded font-medium hover:bg-blue-700 transition-colors"
            onClick={() => handleNavigation('awareness')}
          >
            Awareness Analysis
          </button>
          <button 
            className="px-6 py-3 bg-amber-600 text-white rounded font-medium hover:bg-amber-700 transition-colors"
            onClick={() => handleNavigation('health')}
          >
            Healthcare Experience
          </button>
          <button 
            className="px-6 py-3 bg-emerald-600 text-white rounded font-medium hover:bg-emerald-700 transition-colors"
            onClick={() => handleNavigation('intent')}
          >
            Vaccination Intent
          </button>
          <button 
            className="px-6 py-3 bg-purple-600 text-white rounded font-medium hover:bg-purple-700 transition-colors"
            onClick={() => handleNavigation('demographics')}
          >
            Demographics Deep Dive
          </button>
        </div>
      </div>

      {/* Document Footer */}
      <div className="mt-12 pt-8 border-t border-gray-200 text-center text-xs text-gray-500">
        <p><strong>Document Prepared:</strong> October 2025</p>
        <p><strong>Analysis Basis:</strong> Population-representative survey data with MRP adjustments</p>
        <p><strong>Strategic Framework:</strong> Three-dimensional segmentation (Knowledge × Health Access × Intent)</p>
        <p><strong>Implementation Timeline:</strong> 24-month phased approach with quarterly milestones</p>
      </div>
    </div>
  );
};

export default HEPTEnhancedExecutiveDashboard;
